import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI, projectAPI, threadAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDistanceToNow } from 'date-fns';
import { Bookmark, FolderOpen, MessageSquare, Loader2 } from 'lucide-react';

export default function Bookmarks() {
  const [currentUser, setCurrentUser] = useState(null);
  const [bookmarkedProjects, setBookmarkedProjects] = useState([]);
  const [bookmarkedThreads, setBookmarkedThreads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const userRes = await authAPI.getMe();
      setCurrentUser(userRes.data);

      const bookmarks = userRes.data.bookmarks || {};
      
      // Fetch bookmarked projects
      if (bookmarks.projects?.length > 0) {
        const projectsRes = await projectAPI.getAll({ limit: 100 });
        setBookmarkedProjects(
          projectsRes.data.filter(p => bookmarks.projects.includes(p.project_id))
        );
      }

      // Fetch bookmarked threads
      if (bookmarks.threads?.length > 0) {
        const threadsRes = await threadAPI.getAll({ limit: 100 });
        setBookmarkedThreads(
          threadsRes.data.filter(t => bookmarks.threads.includes(t.thread_id))
        );
      }
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bookmarks-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Bookmarks</h1>

        <Tabs defaultValue="projects">
          <TabsList className="mb-6">
            <TabsTrigger value="projects" className="gap-2">
              <FolderOpen className="w-4 h-4" />
              Projects ({bookmarkedProjects.length})
            </TabsTrigger>
            <TabsTrigger value="threads" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Threads ({bookmarkedThreads.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {bookmarkedProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bookmark className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No bookmarked projects</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Bookmark projects to save them for later
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {bookmarkedProjects.map((project) => (
                  <Link key={project.project_id} to={`/projects/${project.project_id}`}>
                    <Card className="card-hover h-full">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">{project.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{project.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>by {project.author_name}</span>
                          <span>↑ {project.upvotes - project.downvotes}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="threads">
            {bookmarkedThreads.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bookmark className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No bookmarked threads</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Bookmark threads to follow discussions
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {bookmarkedThreads.map((thread) => (
                  <Link key={thread.thread_id} to={`/forum/thread/${thread.thread_id}`}>
                    <Card className="card-hover">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-2">{thread.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{thread.content}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>by {thread.author_name}</span>
                          <span>↑ {thread.upvotes - thread.downvotes}</span>
                          <span>{thread.reply_count} replies</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
